import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Card,
  CardContent,
  CardActions,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search, Message, People } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';

// FEAT-038 (MVP-5.6, MVP-5.7) — Caregivers do NOT browse a public careseeker
// directory. They see a curated follow-up list of careseekers who have
// messaged or booked them (the relationship-linked set enforced by the API at
// GET /users/patients), so they can re-engage and build the relationship.

interface ConnectedCareseeker {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatar?: string | null;
}

interface UsersPatientsResponse {
  success: boolean;
  data: ConnectedCareseeker[];
}

const displayName = (careseeker: ConnectedCareseeker): string =>
  `${careseeker.firstName || ''} ${careseeker.lastName || ''}`.trim() || careseeker.email;

const SearchPatientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [careseekers, setCareseekers] = useState<ConnectedCareseeker[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadConnections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The API returns only careseekers linked to this caregiver via prior
      // messaging or booking (FEAT-038 role boundary).
      const response = await apiClient.get<UsersPatientsResponse>('/users/patients?page=1&limit=100');
      setCareseekers(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      setError('We could not load your connections. Please try again.');
      setCareseekers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return careseekers;
    }
    return careseekers.filter((careseeker) => {
      return (
        displayName(careseeker).toLowerCase().includes(term) ||
        careseeker.email.toLowerCase().includes(term)
      );
    });
  }, [careseekers, searchTerm]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="careseeker-connections-page">
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Careseeker Connections
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          data-testid="careseeker-connections-intro"
        >
          These are careseekers who have messaged or booked you. Keep in touch
          and follow up — Careva does not let caregivers browse a public
          directory of careseekers.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Filter your connections by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="careseeker-connections-filter"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="outlined"
              fullWidth
              onClick={loadConnections}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <People />}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {filtered.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            {filtered.length} connection{filtered.length !== 1 ? 's' : ''}
          </Typography>
          <Divider />
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filtered.length > 0 ? (
        <Grid container spacing={3}>
          {filtered.map((careseeker) => (
            <Grid item xs={12} sm={6} md={4} key={careseeker.id}>
              <Card
                sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                data-testid="careseeker-connection-card"
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {displayName(careseeker).charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" component="div">
                        {displayName(careseeker)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {careseeker.email}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Message />}
                    onClick={() => navigate('/messages')}
                  >
                    Message
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box textAlign="center" py={4} data-testid="careseeker-connections-empty">
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No connections yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Careseekers will appear here after they message you or book one of
            your services. There's no public careseeker directory to browse.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default SearchPatientsPage;
